import React, { useEffect, useMemo, useRef } from "react";

import { CircleMarker } from "react-leaflet";

import MapConfig from "services/map/MapConfig";
import MapTooltipLabel from "components/MapTooltipLabel";

type Props = {
  label?: string | JSX.Element | null;
  center: [number, number];
  onClick: Function;
  selected: boolean;
  options?: {
    withLabel?: boolean;
    withLabelBackground?: boolean;
    selection_color?: string;
    offset?: { x: number; y: number };
    background?: {
      value?: number;
      total?: number;
      out?: number;
      in?: number;
      config?: {
        mainColor: string;
        unselectedColor: string;
        minColor: string;
        valueColors: {};
        unselectedValueColor: string;
      };
    };
    apiType: string;
  };
  zoom: number;
  disabled: boolean;
  interactive?: boolean;
  labelLocation?: [number, number];
  bringToFront?: boolean;
};

function MapPoint({
  label,
  center,
  onClick,
  selected,
  options,
  zoom,
  disabled,
  interactive = true,
  labelLocation = undefined,
  bringToFront = false,
}: Props): JSX.Element {
  const circleRadius = useMemo(() => {
    const defaultRadius = MapConfig?.flow?.point?.radius?.default || 12;
    return MapConfig?.flow?.point?.radius?.[`zoom${zoom}`] || defaultRadius;
  }, [zoom]);

  const color = useMemo(() => {
    const unselectedColor = MapConfig?.flow?.point?.color?.unselected;
    const selectedColor = MapConfig?.flow?.point?.color?.selected;

    return selected ? selectedColor : unselectedColor;
  }, [selected]);

  const fillColor = useMemo(() => {
    if (options?.apiType === "VIRTUAL") {
      return (
        options?.selection_color || MapConfig?.flow?.point?.fillColor?.selected
      );
    }
    const unselectedFillColor = MapConfig?.flow?.point?.fillColor?.unselected;
    const selectedFillColor =
      options?.selection_color || MapConfig?.flow?.point?.fillColor?.selected;

    return selected ? selectedFillColor : unselectedFillColor;
  }, [options?.apiType, options?.selection_color, selected]);

  const weight = useMemo(() => {
    const defaultWeight = MapConfig?.flow?.point?.weight?.default || 3;
    return MapConfig?.flow?.point?.weight?.[`zoom${zoom}`] || defaultWeight;
  }, [zoom]);

  const tooltipOffset = useMemo(() => {
    // For Points, we use offset in pixel [x, y]
    const xOffset = options?.offset?.x || 0;
    const yOffset = options?.offset?.y || 35;

    return [xOffset, yOffset];
  }, [options?.offset]);

  function handleOnClick() {
    if (disabled) return null;
    return onClick();
  }

  const circleRef = useRef<CircleMarker | null>(null);

  /* Bring the circle to the front on first rendering
   * it will never re-trigger since it checks for the ref or the boolean bringToFront
   * to update which will not
   */
  useEffect(() => {
    if (bringToFront) {
      circleRef.current?.leafletElement?.bringToFront();
    }
  }, [bringToFront, circleRef]);

  return (
    <>
      <CircleMarker
        ref={circleRef}
        // @ts-ignore
        center={center}
        radius={circleRadius}
        fillColor={fillColor}
        fillOpacity={1}
        weight={weight}
        color={color}
        onclick={handleOnClick}
        interactive={interactive}
      />
      {options?.withLabel ? (
        <MapTooltipLabel
          type="point"
          position={labelLocation ?? center}
          offset={tooltipOffset}
          label={label}
          selected={selected}
          background={
            options?.withLabelBackground ? options?.background : undefined
          }
          onClick={onClick}
          disabled={disabled}
          interactive={interactive}
        />
      ) : null}
    </>
  );
}

MapPoint.defaultProps = {
  onClick: () => {},
  options: {},
  selected: false,
};

export default MapPoint;
